import type { UnitType } from '@racemap/utilities/types/units';
import classnames from 'classnames';
import type React from 'react';
import { sendAnalyticsEvent } from 'src/lib/googleAnalytics';
import type { StarterType } from 'src/lib/player/store';
import {
  formatDistance,
  formatTimeDurationHuman,
  noop,
  onlyMouseEvents,
} from 'src/lib/player/utils';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import { SelectionControls } from './SelectionControls';

const SEEN_DEVICE_CLASSES: Array<string | null> = ['Mobile', 'Tracker', 'TrackBox'];

export function formatStarterLabel(starter: StarterType): string {
  if (starter.startNumber != null && starter.startNumber !== '') {
    if (starter.name != null && starter.name !== '') {
      return `${starter.startNumber}. ${starter.name}`;
    }
    return `${starter.startNumber}.`;
  }
  if (starter.name != null && starter.name !== '') {
    return starter.name;
  }
  return '👻';
}

export type StarterListItemProps = {
  starter: StarterType;
  starterOpacity: number;
  starterOffset: number;
  starterTimestamp: number;
  isSelected: boolean;
  isHighlighted: boolean;
  isVisible: boolean;
  isLive: boolean;
  now: number;
  eventNames: string | null;
  unitType: UnitType;
  onClick?: (starter: StarterType, isSelected: boolean) => void;
  onToggleSelected?: () => void;
  onToggleHidden?: () => void;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
};

export default function StarterListItem({
  starter,
  starterOpacity,
  starterOffset,
  starterTimestamp,
  isSelected,
  isHighlighted,
  isVisible,
  isLive,
  now,
  eventNames,
  unitType,
  onClick = noop,
  onToggleSelected = noop,
  onToggleHidden = noop,
  onPointerEnter = noop,
  onPointerLeave = noop,
}: StarterListItemProps): JSX.Element {
  const isSelectedOrHighlighted = isSelected || isHighlighted;
  const label = formatStarterLabel(starter);

  function handleClick() {
    if (isSelectedOrHighlighted) {
      sendAnalyticsEvent('starter-list-item-click', { description: 'starter-unselect' });
    } else {
      sendAnalyticsEvent('starter-list-item-click', { description: 'starter-select' });
    }
    onToggleSelected();
    onClick(starter, !isSelected);
  }

  function handleHideClick(
    ev:
      | React.MouseEvent<SVGSVGElement>
      | React.TouchEvent<SVGSVGElement>
      | React.KeyboardEvent<SVGSVGElement>,
  ) {
    ev.stopPropagation();
    onToggleHidden();
  }

  return (
    <div
      className={classnames('starter-list-item', {
        selected: isSelectedOrHighlighted,
      })}
      {...onClickGenerator(handleClick)}
      onPointerEnter={onlyMouseEvents(onPointerEnter)}
      onPointerLeave={onlyMouseEvents(onPointerLeave)}
    >
      <div style={{ opacity: 0.5 * starterOpacity + 0.5 }}>
        <div className="starter-color" style={{ backgroundColor: starter.markerColor.hex }} />
        <div className="starter-text">
          <div className="starter-name" title={label}>
            <div className="starter-name-content">{label}&nbsp;</div>
          </div>
          {(eventNames != null || starterOffset >= 0) && (
            <div className="starter-info">
              {eventNames != null && <span title={eventNames}>{eventNames}</span>}
              {starterOffset >= 0 && <span>{formatDistance(starterOffset, unitType)}</span>}
              {isLive &&
                starterTimestamp > 0 &&
                SEEN_DEVICE_CLASSES.includes(starter.deviceClass) && (
                  <span>{formatTimeDurationHuman(now - starterTimestamp)}</span>
                )}
            </div>
          )}
        </div>
        <SelectionControls
          isSelected={isSelected}
          isVisible={isVisible}
          onHideClick={handleHideClick}
        />
      </div>
    </div>
  );
}
